import * as Yup from "yup";

// Common validation patterns
export const phoneRegExp = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
export const emailRegExp = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

/** Strip whitespace from phone input before validation and save (e.g. "01992 444274"). */
export function normalizePhoneInput(value: unknown): string {
  if (value == null) return "";
  return String(value).replace(/\s/g, "");
}

/** Optional string fields that may be null from the API (form binds ""; API may receive null on submit). */
export const optionalNullableString = Yup.string()
  .nullable()
  .transform((value) => value ?? "")
  .optional();

// Common validation schemas
export const phoneValidation = Yup.string()
  .transform(normalizePhoneInput)
  .required("Required")
  .test("phone", "Invalid phone number", (value) => {
    if (!value) return false;
    const digits = value.replace(/\D/g, "");
    return digits.length >= 10 && digits.length <= 15;
  });

export const emailValidation = Yup.string()
  .required("Required")
  .email("Invalid email address");

export const requiredValidation = Yup.string().required("Required");

export const optionalEmailValidation = Yup.string()
  .email("Invalid email address")
  .optional();

// Company validation schema
export const companyValidationSchema = Yup.object().shape({
  phone: phoneValidation,
  contactEmail: emailValidation,
  addressLine1: requiredValidation,
  addressLine2: optionalNullableString,
  addressLine3: optionalNullableString,
  town: requiredValidation,
  county: optionalNullableString,
  country: requiredValidation,
  postcode: requiredValidation,
  website: Yup.string()
    .transform((value) => (typeof value === "string" ? value.trim() : value))
    .test(
      "website",
      "Invalid Website",
      (value) =>
        !value ||
        /^(http[s]?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}(\/[a-zA-Z0-9+&@#/%?=~_|!:,.;-]*)?$/.test(value)
    ),
  invitationEmail: emailValidation,
});

// User validation schema
export const userValidationSchema = Yup.object().shape({
  firstName: requiredValidation,
  lastName: requiredValidation,
  email: emailValidation,
  phone: phoneValidation.optional(),
});

// Password validation schema
export const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .required("Required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[0-9]/, "Password must contain at least one number"),
  confirmPassword: Yup.string()
    .required("Required")
    .oneOf([Yup.ref("password")], "Passwords must match"),
});

// Address validation schema
export const addressValidationSchema = Yup.object().shape({
  addressLine1: requiredValidation,
  addressLine2: optionalNullableString,
  addressLine3: optionalNullableString,
  town: requiredValidation,
  county: optionalNullableString,
  country: requiredValidation,
  postcode: requiredValidation,
}); 
